import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  GeoJSON,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "../styles/UtahPowergrid.css"; // Import new styles
// import "../styles/Geothermal.css"; // Removed old styles
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faAtom,
  faFire,
  faFlask,
  faIndustry,
  faLightbulb,
  faChartLine,
  faCogs,
  faNetworkWired,
  faBatteryFull,
  faGlobe,
  faUsers,
  faCalendarCheck,
  faCheckCircle,
  faClock,
  faRocket,
} from "@fortawesome/free-solid-svg-icons";
import powerGridData from "./PowerGrid.json";
import { SUBSTATION_COORDS } from "./substationCoordinates";

// Fix for Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Custom icons for different facility types
const geothermalIcon = new L.Icon({
  iconUrl: "/img/geothermal-spots.png",
  iconSize: [35, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -30],
});

// Default icon for other facility types
const defaultIcon = new L.Icon({
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const getIcon = (facilityType) => {
  const type = facilityType?.toUpperCase();
  if (type && type.includes("GEOTHERMAL")) return geothermalIcon;
  return defaultIcon;
};

const isMobileView = () => window.innerWidth <= 778;

const MapFocus = ({ location }) => {
  const map = useMap();

  useEffect(() => {
    if (location) {
      map.flyTo(
        location,
        isMobileView() ? 6 : 9,
        { animate: true, duration: 1.5 }
      );
    }
  }, [location, map]);

  return null;
};


const UtahPowergrid = () => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [utahBoundary, setUtahBoundary] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const markerRefs = useRef([]);

  useEffect(() => {
    fetch("/utah-boundary.geojson")
      .then((response) => response.json())
      .then((data) => setUtahBoundary(data))
      .catch((error) => console.error("Error loading Utah boundary:", error));
  }, []);

  const handleLocationClick = (station, index) => {
    const coords = SUBSTATION_COORDS[station["Substation Name"]];
    if (!coords) return;

    setSelectedLocation({ lat: coords[0], lng: coords[1] });

    // Ensure map flies to location and popup opens
    // We rely on MapFocus to handle the flyTo
    // We imperatively open the popup
    setTimeout(() => {
      if (markerRefs.current[index]) {
        markerRefs.current[index].openPopup();
      }
    }, 100); // Slight delay to ensure fly animation starts
  };


  // Filter data
  const filteredData = powerGridData.filter((station) =>
    station["Substation Name"].toLowerCase().includes(searchTerm.toLowerCase()) ||
    station.Category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    station.Region.toLowerCase().includes(searchTerm.toLowerCase()) ||
    station["Primary Operator"].toLowerCase().includes(searchTerm.toLowerCase())
  );


  return (
    <div className="powergrid-container">
      <LandingSection
        data={{
          imageName: "powergrid.webp",
          title: "Operation Gigawatt - Utah Power Grid",
          visible: "",
        }}
      />

      {/* Intro Section */}
      <section className="verticals-section">
        <div className="content-wrapper">
          <div className="section-title-wrapper">
            <FontAwesomeIcon icon={faBolt} className="section-icon" />
            <h2 className="section-title">Operation Gigawatt</h2>
          </div>

          <div className="intro-grid">
            <div className="intro-text">
              <p>
             Operation Gigawatt is a plan to make sure Utah has enough electricity as old power plants close and power use grows.
            </p>
            <p>
              Launched by Governor Spencer Cox in October 2024, this initiative aims
              to double Utah's power generation capacity by 2034. The initiative
              represents a pivot from "Any of the Above" to a "More of the Above"
              energy strategy.
            </p>
            </div>
            <div className="intro-highlight">
              <h3>⚡ The Goal: Double Capacity</h3>
              <p className="text-white/90">
                To meet the needs of a growing population and a booming economy, Utah must build as much power generation in the next 10 years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Projects Section */}
      <section className="verticals-section dark-bg">
        <div className="content-wrapper">
          <div className="section-title-wrapper">
            <FontAwesomeIcon icon={faRocket} className="section-icon" />
            <h2 className="section-title">Key Pillars</h2>
          </div>

          <div className="cards-grid">
            {/* Advanced Nuclear */}
            <div className="feature-card card-nuclear">
              <div className="feature-card-header">
                <FontAwesomeIcon icon={faAtom} className="card-icon" />
                <h3 className="card-title">Advanced Nuclear</h3>
              </div>
              <p className="card-description">
                Pioneering the future of nuclear energy. TerraPower is currently assessing sites in Utah for a potential
                Natrium advanced nuclear plant, bringing safe, carbon-free baseload power to the grid.
              </p>
            </div>

            {/* Geothermal */}
            <div className="feature-card card-geothermal">
              <div className="feature-card-header">
                <FontAwesomeIcon icon={faFire} className="card-icon" />
                <h3 className="card-title">Geothermal Expansion</h3>
              </div>
              <p className="card-description">
                Tapping into the earth's heat. Fervo Energy's Cape Station project is scaling up next-generation geothermal,
                expected to supply 90 MW by 2026 and reaching 400 MW by 2028.
              </p>
            </div>

            {/* Hydrogen Hub */}
            <div className="feature-card card-hydrogen">
              <div className="feature-card-header">
                <FontAwesomeIcon icon={faBatteryFull} className="card-icon" />
                <h3 className="card-title">Hydrogen Hub</h3>
              </div>
              <p className="card-description">
                The Advanced Clean Energy Storage (ACES) hub in Delta is a world-first, utilizing salt caverns to store
                green hydrogen for massive-scale seasonal energy shifting and grid stability.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* What is the Project Section */}
      <section className="verticals-section">
        <div className="content-wrapper">
          <div className="section-title-wrapper">
            <FontAwesomeIcon icon={faCogs} className="section-icon" />
            <h2 className="section-title">Strategic Components</h2>
          </div>

          <div className="text-center max-w-4xl mx-auto mb-12">
            <p className="text-lg text-gray-600">
              Operation Gigawatt is not a single project but a framework coordinating infrastructure, policy, and innovation across four key areas:
            </p>
          </div>

          <div className="cards-grid">
            <div className="feature-card card-transmission">
              <div className="feature-card-header">
                <FontAwesomeIcon icon={faNetworkWired} className="card-icon" />
                <h3 className="card-title">Transmission</h3>
              </div>
              <p className="card-description">
                Expanding high-voltage transmission lines to transport power from remote generation sites to population centers along the Wasatch Front.
              </p>
            </div>

            <div className="feature-card card-production">
              <div className="feature-card-header">
                <FontAwesomeIcon icon={faIndustry} className="card-icon" />
                <h3 className="card-title">Production</h3>
              </div>
              <p className="card-description">
                Aggressively developing new generation sources, with a focus on dispatchable, 24/7 resources like SMRs and Enhanced Geothermal.
              </p>
            </div>

            <div className="feature-card card-policy">
              <div className="feature-card-header">
                <FontAwesomeIcon icon={faChartLine} className="card-icon" />
                <h3 className="card-title">Policy Reform</h3>
              </div>
              <p className="card-description">
                Streamlining permitting processes and establishing "Energy Zones" to reduce red tape and accelerate project development.
              </p>
            </div>

            <div className="feature-card card-innovation">
              <div className="feature-card-header">
                <FontAwesomeIcon icon={faLightbulb} className="card-icon" />
                <h3 className="card-title">Innovation</h3>
              </div>
              <p className="card-description">
                Investing in R&D to bridge the gap between laboratory breakthroughs and grid-scale implementation of new technologies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Drivers Section */}
      <section className="verticals-section dark-bg">
        <div className="content-wrapper">
          <div className="section-title-wrapper">
            <FontAwesomeIcon icon={faUsers} className="section-icon" />
            <h2 className="section-title">Why Now?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="driver-card">
              <div className="text-amber-500 text-3xl mt-1">
                <FontAwesomeIcon icon={faBolt} />
              </div>
              <div>
                <h4 className="text-xl font-bold text-blue-900 mb-2">Load Growth</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Explosive demand from data centers, AI computing, and electrification of transportation is pushing the grid to its limits.
                </p>
              </div>
            </div>

            <div className="driver-card">
              <div className="text-red-500 text-3xl mt-1">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div>
                <h4 className="text-xl font-bold text-blue-900 mb-2">Retiring Resources</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  67% of Utah's current firm baseload capacity (primary coal) is scheduled to retire within the next 20 years, creating a massive supply gap.
                </p>
              </div>
            </div>

            <div className="driver-card">
              <div className="text-emerald-500 text-3xl mt-1">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div>
                <h4 className="text-xl font-bold text-blue-900 mb-2">Economic Security</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Maintaing Utah's competitive advantage of low electricity rates is crucial for continued economic prosperity and job growth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="verticals-section">
        <div className="content-wrapper">
          <div className="section-title-wrapper">
            <FontAwesomeIcon icon={faCalendarCheck} className="section-icon" />
            <h2 className="section-title">Project Timeline</h2>
          </div>

          <div className="timeline-container">
            <table className="timeline-table">
              <thead>
                <tr>
                  <th>Milestone</th>
                  <th>Target Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Official Launch</td>
                  <td>October 2024</td>
                  <td><span className="status-badge status-completed"><FontAwesomeIcon icon={faCheckCircle} /> Completed</span></td>
                </tr>
                <tr>
                  <td>Site Selection for Nuclear</td>
                  <td>Late 2025 - Early 2026</td>
                  <td><span className="status-badge status-ongoing"><FontAwesomeIcon icon={faClock} /> Ongoing</span></td>
                </tr>
                <tr>
                  <td>Geothermal Integration</td>
                  <td>2026 - 2028</td>
                  <td><span className="status-badge status-progress"><FontAwesomeIcon icon={faFire} /> In Progress</span></td>
                </tr>
                <tr>
                  <td>Nuclear Manufacturing Hub</td>
                  <td>2026 - 2030</td>
                  <td><span className="status-badge status-planned"><FontAwesomeIcon icon={faIndustry} /> Planned</span></td>
                </tr>
                <tr>
                  <td><strong>The "Doubling" Target</strong></td>
                  <td><strong>2034</strong></td>
                  <td><span className="status-badge status-goal"><FontAwesomeIcon icon={faRocket} /> End Goal</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Long-Term Results Section */}
      <section className="verticals-section dark-bg">
        <div className="content-wrapper">
          <div className="section-title-wrapper">
            <FontAwesomeIcon icon={faGlobe} className="section-icon" />
            <h2 className="section-title">Long-Term Vision</h2>
          </div>

          <div className="cards-grid">
            <div className="feature-card card-nuclear border-t-4 border-l-0 border-t-red-500">
              <div className="feature-card-header">
                <FontAwesomeIcon icon={faAtom} className="card-icon text-red-500" />
                <h3 className="card-title">Clean Backbone</h3>
              </div>
              <p className="card-description">
                Transitioning to a grid anchored by SMRs and Geothermal, providing the reliability of coal with near-zero emissions.
              </p>
            </div>

            <div className="feature-card card-transmission border-t-4 border-l-0 border-t-blue-500">
              <div className="feature-card-header">
                <FontAwesomeIcon icon={faGlobe} className="card-icon text-blue-500" />
                <h3 className="card-title">Global Energy Hub</h3>
              </div>
              <p className="card-description">
                Exporting not just power, but technology. Utah aims to be a global leader in advanced energy manufacturing and expertise.
              </p>
            </div>

            <div className="feature-card card-hydrogen border-t-4 border-l-0 border-t-emerald-500">
              <div className="feature-card-header">
                <FontAwesomeIcon icon={faBatteryFull} className="card-icon text-emerald-500" />
                <h3 className="card-title">Grid Resilience</h3>
              </div>
              <p className="card-description">
                Serving as the "battery" for the West through massive-scale hydrogen storage and enhanced transmission connectivity.
              </p>
            </div>

            <div className="feature-card card-geothermal border-t-4 border-l-0 border-t-amber-500">
              <div className="feature-card-header">
                <FontAwesomeIcon icon={faUsers} className="card-icon text-amber-500" />
                <h3 className="card-title">Economic Impact</h3>
              </div>
              <p className="card-description">
                Creating 3,000–5,000 high-paying technical jobs and securing Utah's energy independence for decades to come.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Data Sources Section */}
      <section className="verticals-section">
        <div className="content-wrapper">
          <h2 className="text-2xl font-bold text-center text-blue-900 mb-8">Data Sources & Official Links</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://governor.utah.gov/press/news-release-gov-cox-unveils-operation-gigawatt/" target="_blank" rel="noreferrer"
              className="px-6 py-3 border-2 border-blue-800 text-blue-800 rounded-lg hover:bg-blue-800 hover:text-white transition-colors font-semibold">
              Governor's Announcement
            </a>
            <a href="https://energy.utah.gov/homepage/about-us/operation-gigawatt/" target="_blank" rel="noreferrer"
              className="px-6 py-3 border-2 border-blue-800 text-blue-800 rounded-lg hover:bg-blue-800 hover:text-white transition-colors font-semibold">
              OED - Operation Gigawatt
            </a>
            <a href="https://www.utahcdmag.com/mission-in-progress" target="_blank" rel="noreferrer"
              className="px-6 py-3 border-2 border-blue-800 text-blue-800 rounded-lg hover:bg-blue-800 hover:text-white transition-colors font-semibold">
              Utah CDMAG Article
            </a>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="verticals-section dark-bg">
        <div className="content-wrapper">
          <div className="section-title-wrapper">
            <h2 className="section-title">Interactive Grid Map</h2>
          </div>

          <div className="map-dashboard-restored flex flex-col lg:flex-row h-[120vh] border border-gray-300 rounded-xl overflow-hidden shadow-lg bg-white">
            {/* List Container - RESTORED TABLE STYLE */}
            <div className="w-full lg:w-[40%] flex flex-col border-r border-gray-300 bg-white">
              <div className="p-4 border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                <h3 className="font-bold text-blue-900 text-lg mb-2">Substations ({filteredData.length})</h3>
                <input
                  type="text"
                  placeholder="Search facilities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Restored Table Structure */}
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {/* Table Header - Sticky */}
                <div className="grid grid-cols-12 gap-2 bg-red-100 text-red-800 p-3 font-bold text-xs uppercase tracking-wider sticky top-0 z-10 border-b border-pink-700">
                  <div className="col-span-4">Substation</div>
                  <div className="col-span-3">Type</div>
                  <div className="col-span-2">kV</div>
                  <div className="col-span-3">Region</div>
                </div>

                {filteredData.length > 0 ? (
                  filteredData.map((station, index) => (
                    <div
                      key={index}
                      onClick={() => handleLocationClick(station, index)}
                      className={`grid grid-cols-12 gap-2 p-3 border-b border-pink-100 items-center hover:bg-red-50 hover:scale-[1.005] transition-all duration-200 cursor-pointer group text-sm ${selectedLocation && selectedLocation.lat === SUBSTATION_COORDS[station["Substation Name"]]?.[0] ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''}`}
                    >
                      <div className="col-span-4 font-bold text-blue-900 group-hover:text-blue-700 transition-colors">
                        {station["Substation Name"]}
                      </div>
                      <div className="col-span-3 text-xs font-bold text-red-800 uppercase tracking-wide truncate">
                        {station.Category}
                      </div>
                      <div className="col-span-2 text-blue-900 font-mono text-xs">
                        {station["Primary Voltage (kV)"]}
                      </div>
                      <div className="col-span-3 text-blue-900 text-xs truncate">
                        {station.Region}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-400 italic">
                    No facilities found.
                  </div>
                )}
              </div>
            </div>

            {/* Map Container */}
            <div className="w-full lg:w-[60%] h-full relative z-0">
              <MapContainer
                center={[39.32098, -111.093731]}
                zoom={7}
                minZoom={6}
                maxBounds={[
                  [30, -105],
                  [50, -120],
                ]}
                maxBoundsViscosity={1.0}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {selectedLocation && (
                  <MapFocus
                    location={[selectedLocation.lat, selectedLocation.lng]}
                  />
                )}

                {utahBoundary && (
                  <GeoJSON
                    data={utahBoundary}
                    style={() => ({
                      color: "#3B82F6",
                      weight: 2,
                      opacity: 0.8,
                      fillColor: "#93C5FD",
                      fillOpacity: 0.1,
                    })}
                  />
                )}

                {filteredData.map((station, index) => {
                  const coords = SUBSTATION_COORDS[station["Substation Name"]];
                  if (!coords) return null;

                  return (
                    <Marker
                      key={index}
                      position={coords}
                      icon={defaultIcon}
                      ref={(el) => (markerRefs.current[index] = el)}
                      eventHandlers={{
                        click: () => {
                          setSelectedLocation({ lat: coords[0], lng: coords[1] });
                        },
                      }}
                    >
                      <Popup>
                        <div className="min-w-[200px]">
                          <h4 className="font-bold text-lg text-blue-900 border-b pb-1 mb-2">{station["Substation Name"]}</h4>
                          <div className="grid gap-1 text-sm text-gray-700">
                            <div className="flex justify-between"><span className="font-semibold">Type:</span> <span>{station.Category}</span></div>
                            <div className="flex justify-between"><span className="font-semibold">Region:</span> <span>{station.Region}</span></div>
                            <div className="flex justify-between"><span className="font-semibold">Voltage:</span> <span>{station["Primary Voltage (kV)"]}</span></div>
                            <div className="flex justify-between"><span className="font-semibold">Operator:</span> <span>{station["Primary Operator"]}</span></div>
                            <div className="mt-2 pt-2 border-t text-xs italic text-gray-500 text-center">
                              {station["Strategic Role / Status"]}
                            </div>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UtahPowergrid;
